import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft,
  Share2,
  Star,
  Globe,
  MapPin,
  Utensils,
  Calendar,
  TrendingUp,
  Award,
  Download,
  ChevronRight,
  ChevronLeft,
  Sparkles,
} from "lucide-react";
import html2canvas from "html2canvas";

const API = process.env.REACT_APP_BACKEND_URL;

const Wrapped = () => {
  const navigate = useNavigate();
  const { year } = useParams();
  const currentYear = year || new Date().getFullYear();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const shareRef = useRef(null);

  const fetchWrapped = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/goals/wrapped/${currentYear}`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching wrapped:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  }, [currentYear]);

  useEffect(() => {
    fetchWrapped();
  }, [fetchWrapped]);

  const handleShare = async () => {
    if (!shareRef.current) return;
    
    try {
      const canvas = await html2canvas(shareRef.current, {
        backgroundColor: '#0a0a0b',
        scale: 2,
      });
      
      canvas.toBlob(async (blob) => {
        if (navigator.share && navigator.canShare({ files: [new File([blob], 'etoile-wrapped.png', { type: 'image/png' })] })) {
          try {
            await navigator.share({
              files: [new File([blob], `etoile-${currentYear}.png`, { type: 'image/png' })],
              title: `Mein Étoile Jahr ${currentYear}`,
              text: `Mein kulinarisches Jahr ${currentYear} mit Étoile 🌟`,
            });
          } catch (err) {
            downloadImage(canvas);
          }
        } else {
          downloadImage(canvas);
        }
      });
    } catch (error) {
      toast.error("Fehler beim Erstellen des Bildes");
    }
  };

  const downloadImage = (canvas) => {
    const link = document.createElement('a');
    link.download = `etoile-wrapped-${currentYear}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success("Bild heruntergeladen!");
  };

  const slides = data?.has_data ? [
    // Slide 1: Intro
    {
      id: "intro",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <Sparkles className="w-16 h-16 text-primary mb-6 animate-pulse" />
          <h1 className="text-4xl md:text-5xl font-light mb-4">
            {data.user?.name || "Gourmet"}
          </h1>
          <p className="text-xl text-muted-foreground">
            Dein kulinarisches Jahr
          </p>
          <p className="text-6xl md:text-8xl font-extralight text-primary mt-4">
            {currentYear}
          </p>
        </div>
      )
    },
    // Slide 2: Total Visits
    {
      id: "visits",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <Utensils className="w-12 h-12 text-primary mb-6" />
          <p className="text-muted-foreground mb-2">Du hast</p>
          <p className="text-7xl md:text-9xl font-extralight gradient-text">
            {data.highlights?.total_visits}
          </p>
          <p className="text-xl text-muted-foreground mt-2">
            Restaurants besucht
          </p>
          <div className="mt-8 flex items-center gap-8">
            <div className="text-center">
              <p className="text-3xl font-light">{data.highlights?.unique_restaurants}</p>
              <p className="text-sm text-muted-foreground">Einzigartige</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-light text-primary">{data.highlights?.total_stars}</p>
              <p className="text-sm text-muted-foreground">Sterne gesammelt</p>
            </div>
          </div>
        </div>
      )
    },
    // Slide 3: Stars Distribution
    {
      id: "stars",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <Star className="w-12 h-12 text-primary fill-primary mb-6" />
          <p className="text-muted-foreground mb-6">Deine Sterne-Verteilung</p>
          <div className="space-y-4 w-full max-w-xs">
            {[3, 2, 1].map((stars) => (
              <div key={`star-dist-${stars}`} className="flex items-center gap-4">
                <div className="flex gap-1 w-20 justify-end">
                  {[...Array(stars)].map((_, i) => (
                    <Star key={`star-icon-${stars}-${i}`} className="w-4 h-4 text-primary fill-primary" />
                  ))}
                </div>
                <div className="flex-1 h-8 bg-muted/40 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-primary/50 to-primary rounded-full transition-all duration-1000"
                    style={{ 
                      width: `${Math.min(100, (data.star_distribution?.[stars] || 0) * 10)}%` 
                    }}
                  />
                </div>
                <span className="w-8 text-right font-light">
                  {data.star_distribution?.[stars] || 0}
                </span>
              </div>
            ))}
          </div>
        </div>
      )
    },
    // Slide 4: Favorite Country
    {
      id: "country",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <Globe className="w-12 h-12 text-primary mb-6" />
          <p className="text-muted-foreground mb-2">Dein Lieblings-Land</p>
          <p className="text-5xl md:text-6xl font-light mt-4">
            {data.highlights?.favorite_country || "—"}
          </p>
          <div className="mt-8 pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground mb-2">
              Du hast {data.highlights?.countries_visited} Länder besucht
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {Object.keys(data.countries || {}).slice(0, 6).map((country) => (
                <span 
                  key={country}
                  className="px-3 py-1 bg-muted/40 rounded-full text-sm"
                >
                  {country}
                </span>
              ))}
            </div>
          </div>
        </div>
      )
    },
    // Slide 5: Favorite Restaurant
    {
      id: "restaurant",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <Award className="w-12 h-12 text-primary mb-6" />
          <p className="text-muted-foreground mb-2">Dein Stammrestaurant</p>
          <p className="text-3xl md:text-4xl font-light mt-4 max-w-md">
            {data.highlights?.most_visited_restaurant || "—"}
          </p>
          {data.highlights?.most_visited_count > 1 && (
            <p className="text-primary mt-4">
              {data.highlights?.most_visited_count}x besucht
            </p>
          )}
          <div className="mt-8 flex items-center gap-2 text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <span>{data.highlights?.favorite_city}</span>
          </div>
        </div>
      )
    },
    // Slide 6: Spending (if available)
    ...(data.spending?.total_eur > 0 || data.spending?.total_usd > 0 ? [{
      id: "spending",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <TrendingUp className="w-12 h-12 text-primary mb-6" />
          <p className="text-muted-foreground mb-2">Du hast investiert</p>
          {data.spending?.total_eur > 0 && (
            <p className="text-5xl md:text-6xl font-light gradient-text">
              {data.spending.total_eur.toLocaleString('de-DE')} €
            </p>
          )}
          {data.spending?.total_usd > 0 && (
            <p className="text-3xl font-light text-muted-foreground mt-2">
              + ${data.spending.total_usd.toLocaleString('en-US')}
            </p>
          )}
          <p className="text-muted-foreground mt-4">
            in kulinarische Erlebnisse
          </p>
          {data.spending?.average_per_visit > 0 && (
            <div className="mt-8 pt-8 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Durchschnittlich <span className="text-primary">{data.spending.average_per_visit.toLocaleString('de-DE')} €</span> pro Besuch
              </p>
            </div>
          )}
        </div>
      )
    }] : []),
    // Slide 7: Summary
    {
      id: "summary",
      content: (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <div className="mb-8">
            <p className="text-sm uppercase tracking-wider text-muted-foreground mb-2">
              Étoile Wrapped
            </p>
            <p className="text-4xl font-light">{currentYear}</p>
          </div>
          
          <div className="grid grid-cols-2 gap-6 w-full max-w-sm">
            <div className="text-center p-4 bg-muted/40 rounded-lg">
              <p className="text-3xl font-light text-primary">{data.highlights?.total_visits}</p>
              <p className="text-xs text-muted-foreground">Besuche</p>
            </div>
            <div className="text-center p-4 bg-muted/40 rounded-lg">
              <p className="text-3xl font-light text-primary">{data.highlights?.total_stars}</p>
              <p className="text-xs text-muted-foreground">Sterne</p>
            </div>
            <div className="text-center p-4 bg-muted/40 rounded-lg">
              <p className="text-3xl font-light">{data.highlights?.countries_visited}</p>
              <p className="text-xs text-muted-foreground">Länder</p>
            </div>
            <div className="text-center p-4 bg-muted/40 rounded-lg">
              <p className="text-3xl font-light">{data.highlights?.unique_restaurants}</p>
              <p className="text-xs text-muted-foreground">Restaurants</p>
            </div>
          </div>
          
          <div className="mt-8 flex items-center gap-2 text-primary">
            <Star className="w-4 h-4 fill-primary" />
            <span className="text-sm font-medium">{data.user?.badge}</span>
          </div>
        </div>
      )
    }
  ] : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="w-12 h-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Lade deinen Jahresrückblick...</p>
        </div>
      </div>
    );
  }

  if (!data?.has_data) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-8 text-center">
        <Calendar className="w-16 h-16 text-muted-foreground mb-6" />
        <h1 className="text-2xl font-light mb-2">Keine Daten für {currentYear}</h1>
        <p className="text-muted-foreground mb-8">
          Du hast in diesem Jahr noch keine Restaurants besucht.
        </p>
        <Button onClick={() => navigate('/restaurants')}>
          Ersten Besuch erfassen
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-20 p-4 flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <Button onClick={handleShare} className="gap-2">
          <Share2 className="w-4 h-4" />
          Teilen
        </Button>
      </header>

      {/* Slides */}
      <div 
        ref={shareRef}
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: '#0a0a0b' }}
      >
        <div className="flex-1 flex items-center justify-center">
          {slides[currentSlide]?.content}
        </div>
        
        {/* Navigation Dots */}
        <div className="fixed bottom-8 left-0 right-0 flex justify-center gap-2 z-20">
          {slides.map((slide, index) => (
            <button
              key={`slide-dot-${slide.id || index}`}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentSlide ? 'bg-primary w-6' : 'bg-muted-foreground/40'
              }`}
            />
          ))}
        </div>
        
        {/* Arrow Navigation */}
        {currentSlide > 0 && (
          <button
            onClick={() => setCurrentSlide(currentSlide - 1)}
            className="fixed left-4 top-1/2 -translate-y-1/2 p-2 bg-muted/40 rounded-full hover:bg-muted/60 transition-all z-20"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        {currentSlide < slides.length - 1 && (
          <button
            onClick={() => setCurrentSlide(currentSlide + 1)}
            className="fixed right-4 top-1/2 -translate-y-1/2 p-2 bg-muted/40 rounded-full hover:bg-muted/60 transition-all z-20"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}
      </div>
    </div>
  );
};

export default Wrapped;
